import React from "react";
import { SkeletonBlock } from "../dashboard/DashboardSkeletons";

const CardHeading = ({ width = 160, subtitle = false, action = true }: { width?: number; subtitle?: boolean; action?: boolean }) => (
  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
    <div>
      <SkeletonBlock width={width} height={18} className={subtitle ? "mb-2" : ""} />
      {subtitle && <SkeletonBlock width={150} height={11} />}
    </div>
    {action && <SkeletonBlock width={84} height={32} />}
  </div>
);

const ChartArea = ({ height, bars }: { height: number; bars?: number[] }) => (
  <div className="d-flex gap-3 pb-3" style={{ height }}>
    <div className="d-flex flex-column justify-content-between">
      {Array.from({ length: 6 }, (_, index) => (
        <SkeletonBlock key={index} width={34} height={10} />
      ))}
    </div>
    {bars ? (
      <div className="flex-fill d-flex align-items-end gap-2 min-w-0">
        {bars.map((bar, index) => (
          <SkeletonBlock key={index} height={`${bar}%`} style={{ flex: "1 1 0", borderRadius: 4 }} />
        ))}
      </div>
    ) : (
      <SkeletonBlock className="flex-fill" height="100%" style={{ borderRadius: 8 }} />
    )}
  </div>
);

const SalesSummarySkeleton = () => (
  <div aria-hidden="true">
    <div className="mb-2">
      <SkeletonBlock width={170} height={14} className="mb-2" />
      <SkeletonBlock width={220} height={11} />
    </div>
    <div className="row g-3 mb-3">
      {[0, 1, 2].map((index) => (
        <div className="col-12 col-md-6 col-xl d-flex" key={index}>
          <div className="card flex-fill mb-0">
            <div className="card-body">
              <SkeletonBlock width={36} height={36} className="mb-2" />
              <SkeletonBlock width="45%" height={11} className="mb-2" />
              <SkeletonBlock width="35%" height={20} className="mb-2" />
              <SkeletonBlock width="70%" height={11} />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="row g-3 mb-4">
      {[0, 1, 2].map((index) => (
        <div className="col-12 col-md-6 col-xl d-flex" key={index}>
          <div className="card flex-fill mb-0">
            <div className="card-body d-flex align-items-center gap-3">
              <SkeletonBlock width={36} height={36} />
              <div className="flex-grow-1">
                <SkeletonBlock width="55%" height={11} className="mb-2" />
                <SkeletonBlock width="40%" height={18} />
              </div>
              <SkeletonBlock width={64} height={30} />
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="row">
      <div className="col-xxl-8 col-xl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={140} subtitle action={false} />
            <div className="d-flex justify-content-end gap-3 mb-4">
              {[0, 1, 2].map((index) => (
                <SkeletonBlock key={index} width={64} height={16} />
              ))}
            </div>
            <div className="row g-4">
              {[0, 1].map((index) => (
                <div className="col-md-6" key={index}>
                  <div className="d-flex">
                    <SkeletonBlock width={64} height={132} style={{ borderRadius: "8px 0 0 8px" }} />
                    <div className="flex-fill p-3 nd-skeleton-panel">
                      <SkeletonBlock width="55%" height={12} className="mb-2" />
                      <SkeletonBlock width="45%" height={24} className="mb-4" />
                      <div className="d-flex align-items-center justify-content-between">
                        <SkeletonBlock width={90} height={16} shape="pill" />
                        <div className="d-flex align-items-end gap-1" style={{ height: 40 }}>
                          {[50, 80, 60, 100, 70].map((bar, barIndex) => (
                            <SkeletonBlock key={barIndex} width={8} height={`${bar}%`} style={{ borderRadius: 2 }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="col-xxl-4 col-xl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-body d-flex flex-column">
            <CardHeading width={140} subtitle action={false} />
            <div className="flex-fill d-flex align-items-end justify-content-center overflow-hidden" style={{ height: 170 }}>
              <SkeletonBlock width={300} height={300} shape="ring" style={{ marginBottom: -150 }} />
            </div>
            <div className="d-flex align-items-center gap-2 mt-3">
              <SkeletonBlock width={70} height={24} />
              <SkeletonBlock width={44} height={18} shape="pill" />
              <SkeletonBlock width={60} height={12} />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={160} subtitle />
            <div className="d-flex align-items-center gap-3">
              <div className="flex-fill">
                {[0, 1].map((index) => (
                  <div key={index} className={`border rounded p-3 d-flex align-items-center gap-3${index === 0 ? " mb-3" : ""}`}>
                    <SkeletonBlock width={40} height={40} shape="circle" />
                    <div className="flex-fill">
                      <SkeletonBlock width="40%" height={12} className="mb-2" />
                      <SkeletonBlock width="55%" height={20} />
                    </div>
                  </div>
                ))}
              </div>
              <SkeletonBlock width={130} height={130} shape="ring" />
            </div>
          </div>
        </div>
      </div>
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={200} action={false} />
            <SkeletonBlock width={140} height={22} className="mb-3" />
            {[92, 74, 58, 40].map((width, index) => (
              <SkeletonBlock key={index} width={`${width}%`} height={38} className={index < 3 ? "mb-2" : ""} />
            ))}
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={190} />
            {[0, 1, 2, 3, 4].map((index) => (
              <div key={index} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
                <SkeletonBlock width={`${50 - index * 4}%`} height={13} />
                <div className="d-flex align-items-center gap-3">
                  <SkeletonBlock width={60} height={13} />
                  <SkeletonBlock width={48} height={20} shape="pill" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="col-xl-6 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={140} />
            <SkeletonBlock width={160} height={20} className="mb-3" />
            <ChartArea height={260} />
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-md-12 d-flex">
        <div className="card flex-fill">
          <div className="card-body">
            <CardHeading width={130} />
            <ChartArea height={300} bars={[40, 55, 35, 70, 50, 82, 46, 64, 58, 76, 44, 68]} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SalesSummarySkeleton;
